import { useEffect } from "react";
import { useLocation } from "@/lib/router-compat";

/**
 * Routes that must NOT be indexed by search engines:
 * - Authenticated app surfaces (private content, no SEO value)
 * - Token-gated public utility pages (booking, unsubscribe, shared links)
 * - Auth flows (login/onboarding/reset)
 *
 * Everything else keeps the default `index, follow` from `index.html`.
 */
const NOINDEX_PREFIXES = [
  "/auth",
  "/reset-password",
  "/onboarding",
  "/payment-success",
  "/unsubscribe",
  "/book/",
  "/shared/",
  "/prospect-form/",
  "/event-checkin/",
  "/collaborator-accept",
  "/dev/",
  // App (private)
  "/dashboard",
  "/chat",
  "/prospects",
  "/clients",
  "/contacts",
  "/emails",
  "/scheduled-emails",
  "/search",
  "/person-search",
  "/auto-search",
  "/events",
  "/checklists",
  "/content",
  "/linkedin-history",
  "/templates",
  "/rules",
  "/calendar",
  "/subscription",
  "/notifications",
  "/settings",
  "/wellbeing",
  "/owner",
];

const DEFAULT_ROBOTS = "index, follow, max-image-preview:large";
const NOINDEX_VALUE = "noindex, nofollow";

function setRobots(value: string) {
  let el = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", "robots");
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

/**
 * Mount once inside <BrowserRouter>. Switches the <meta name="robots">
 * tag based on the current pathname so private/utility pages stay
 * out of search engine indexes.
 */
export default function RouteRobotsController() {
  const { pathname } = useLocation();

  useEffect(() => {
    const shouldNoIndex = NOINDEX_PREFIXES.some(
      (prefix) =>
        pathname === prefix ||
        pathname.startsWith(prefix.endsWith("/") ? prefix : prefix + "/") ||
        pathname === prefix.replace(/\/$/, "")
    );
    setRobots(shouldNoIndex ? NOINDEX_VALUE : DEFAULT_ROBOTS);
  }, [pathname]);

  return null;
}
